import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { getAffectedSections, getAffectedSectionTitles } from '../utils/sectionMapping';

const RegenerationDialog = ({ 
  open, 
  onClose, 
  changes, 
  allSections, 
  onRegenerate,
  isRegenerating = false 
}) => {
  const [regenerationType, setRegenerationType] = useState('selected'); // Default to 'selected' so users can pick
  const [selectedSections, setSelectedSections] = useState([]);

  // Get affected sections based on changes
  const affectedSections = getAffectedSections(changes || []);
  const affectedSectionTitles = getAffectedSectionTitles(changes || []);

  // Initialize selected sections to affected sections when dialog opens
  React.useEffect(() => {
    if (open && regenerationType === 'affected') {
      setSelectedSections(affectedSectionTitles);
    } else if (open && regenerationType === 'all') {
      setSelectedSections(allSections.map(s => s.title));
    }
  }, [open, regenerationType, affectedSectionTitles, allSections]);

  const handleSectionToggle = (sectionTitle) => {
    setSelectedSections(prev => {
      if (prev.includes(sectionTitle)) {
        return prev.filter(title => title !== sectionTitle);
      } else {
        return [...prev, sectionTitle];
      }
    });
  };

  const handleRegenerateClick = () => {
    if (regenerationType === 'all') {
      onRegenerate('all', allSections.map(s => s.title));
    } else if (regenerationType === 'affected') {
      onRegenerate('affected', affectedSectionTitles);
    } else {
      onRegenerate('selected', selectedSections);
    }
  };

  const handleSelectAll = () => {
    if (selectedSections.length === allSections.length) {
      setSelectedSections([]);
    } else {
      setSelectedSections(allSections.map(s => s.title));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <RefreshIcon color="primary" />
          <Typography variant="h6">Regenerate Protocol Sections</Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {changes && changes.length > 0 ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{changes.length}</strong> field(s) have been modified in the clinical intake form. 
                The following sections may need to be regenerated to reflect these changes.
              </Typography>
            </Alert>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                Regeneration Options:
              </Typography>
              
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={regenerationType === 'all'}
                      onChange={() => {
                        setRegenerationType('all');
                        setSelectedSections(allSections.map(s => s.title));
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Regenerate All Sections ({allSections.length})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Regenerate all {allSections.length} protocol sections with the latest clinical intake data
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={regenerationType === 'affected'}
                      onChange={() => {
                        setRegenerationType('affected');
                        setSelectedSections(affectedSectionTitles);
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Regenerate Affected Sections ({affectedSectionTitles.length})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Automatically detect and regenerate only sections affected by the changes
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={regenerationType === 'selected'}
                      onChange={() => setRegenerationType('selected')}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        Regenerate Selected Sections ({selectedSections.length})
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Manually select which sections to regenerate
                      </Typography>
                    </Box>
                  }
                />
              </FormGroup>
            </Box>

            {/* Always show section selection list - it's the main feature */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Select Sections to Regenerate:
                  {regenerationType === 'all' && <Chip label="All Selected" size="small" color="primary" sx={{ ml: 1 }} />}
                  {regenerationType === 'affected' && <Chip label="Affected Selected" size="small" color="warning" sx={{ ml: 1 }} />}
                  {regenerationType === 'selected' && <Chip label={`${selectedSections.length} Selected`} size="small" color="info" sx={{ ml: 1 }} />}
                </Typography>
                <Button size="small" onClick={handleSelectAll}>
                  {selectedSections.length === allSections.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
              
              <Box sx={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
                <List dense>
                  {allSections.map((section) => {
                    const isAffected = affectedSectionTitles.includes(section.title);
                    const isSelected = selectedSections.includes(section.title);
                    
                    return (
                      <ListItem
                        key={section.title}
                        button
                        onClick={() => {
                          handleSectionToggle(section.title);
                          // Auto-switch to 'selected' mode when user manually toggles
                          if (regenerationType !== 'selected') {
                            setRegenerationType('selected');
                          }
                        }}
                        sx={{
                          bgcolor: isAffected && isSelected ? 'warning.light' : isSelected ? 'action.selected' : 'transparent',
                          mb: 0.5,
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: isAffected ? 'warning.light' : 'action.hover'
                          }
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSectionToggle(section.title);
                              // Auto-switch to 'selected' mode when user manually toggles
                              if (regenerationType !== 'selected') {
                                setRegenerationType('selected');
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1}>
                              <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 400 }}>
                                {section.title}
                              </Typography>
                              {isAffected && (
                                <Chip
                                  label="Affected"
                                  size="small"
                                  color="warning"
                                  sx={{ height: 20, fontSize: '0.65rem' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            isAffected && (
                              <Typography variant="caption" color="text.secondary">
                                {affectedSections[section.title]?.changes.length || 0} field change(s) affect this section
                              </Typography>
                            )
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </Box>
            </Box>

            {/* Show summary of what will be regenerated */}
            {selectedSections.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Summary: {selectedSections.length} section(s) will be regenerated
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedSections.slice(0, 10).map((title) => (
                    <Chip
                      key={title}
                      label={title}
                      size="small"
                      color={affectedSectionTitles.includes(title) ? 'warning' : 'default'}
                      icon={affectedSectionTitles.includes(title) ? <WarningIcon /> : <CheckCircleIcon />}
                    />
                  ))}
                  {selectedSections.length > 10 && (
                    <Chip
                      label={`+${selectedSections.length - 10} more`}
                      size="small"
                      color="default"
                    />
                  )}
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Alert severity="warning">
            No changes detected. All sections are up to date with the clinical intake form.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isRegenerating}>
          Cancel
        </Button>
        <Button
          onClick={handleRegenerateClick}
          variant="contained"
          color="primary"
          startIcon={isRegenerating ? <CircularProgress size={16} /> : <RefreshIcon />}
          disabled={isRegenerating || selectedSections.length === 0}
        >
          {isRegenerating ? 'Regenerating...' : 'Regenerate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegenerationDialog;

